import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import type { JSX } from "react";

import type { ExpertAssistMessage } from "../../shared-types";

import { getLastMessageOfType } from "../../state";
import { botMessageTypes, isFromExpert, isProactiveGuidanceType } from "../../utils";
import { TimelineItem } from "../timeline-item";
import { generateTimelineItemFromType } from "./generateTimelineItemForType";

export function getScrollableTimelineMessages(messages: ExpertAssistMessage[]) {
    return messages.filter((message) => !isProactiveGuidanceType(message.type));
}

interface GenerateTimelineItemProps {
    messages: ExpertAssistMessage[];
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export function generateTimelineItems({ messages, scrollRef, logger, gaiaPayloadProps }: GenerateTimelineItemProps) {
    const items: JSX.Element[] = [];
    const scrollableMessages = getScrollableTimelineMessages(messages);

    const lastBotMessage = getLastMessageOfType(scrollableMessages, botMessageTypes);
    const activeMessages = scrollableMessages.filter((msg) => msg.status === "active");
    const lastProactiveMessage = getLastMessageOfType(activeMessages, ["Proactive", "Default", "SessionSummary"]);

    let loadingBotMessageItem = null;
    if (scrollableMessages.length) {
        const mostRecentMessage = scrollableMessages[scrollableMessages.length - 1];
        const isLoadingBotMessage = isFromExpert(mostRecentMessage.type) && !mostRecentMessage.metadata?.retryPayload;
        const showIdleBotIcon = isFromExpert(mostRecentMessage.type) && !!mostRecentMessage.metadata?.retryPayload;
        if (isLoadingBotMessage || showIdleBotIcon) {
            loadingBotMessageItem = (
                <TimelineItem
                    isLastBotMessage
                    isLastProactiveMessage
                    isLoading={isLoadingBotMessage}
                    key="LoadingBotMessage"
                    type="Proactive"
                    logger={logger}
                    gaiaPayloadProps={gaiaPayloadProps}
                />
            );
        }
    }

    items.push(
        ...scrollableMessages.map((msg) => (
            <TimelineItem
                isLastBotMessage={!loadingBotMessageItem && msg.id === lastBotMessage}
                isLastProactiveMessage={msg.id === lastProactiveMessage}
                message={msg}
                type={msg.type}
                isLoading={false}
                key={`timeline-item-${msg.id}-${msg.type}`}
                scrollRef={scrollRef}
                metadata={msg.metadata}
                logger={logger}
                gaiaPayloadProps={gaiaPayloadProps}
            >
                {generateTimelineItemFromType(msg)}
            </TimelineItem>
        )),
    );

    if (loadingBotMessageItem) items.push(loadingBotMessageItem);

    return items;
}
